/**
 * VX-4395: page-side "idle-but-healthy" wedge detector.
 *
 * VX-4284's worker-side pong-withhold gate (`PixelWorkerConnection.getPongWithholdReason`) cannot
 * see this variant: once a connection's `activeRequests` drains to zero the worker pongs healthy,
 * even if it silently stopped delivering while a viewport is still waiting on a
 * `load-complete`/`load-error` for that stack (inc-purple-inferior: the worker-side pong stayed
 * healthy for the entire 28-case cliff). Only the page knows which stacks are still awaiting
 * frames — `BaseImagingProvider.status === 'loading'` means exactly that, and flips to
 * `'complete'`/`'error'` the moment the worker finishes, so a legitimately idle reader (a fully
 * loaded study the radiologist is just viewing) never matches this gate.
 *
 * Mirrors the shape of `PixelWorkerConnection`'s `DELIVERY_STALL_MS` / `COLD_START_STALL_MS`
 * gates, but evaluated from the page's own frame-delivery clock (`msSinceLastFrameDelivered`) so a
 * worker that has gone quiet cannot self-report healthy.
 */

/** A `loading` provider's delivery state, as read from `WorkerLoadedImagingProvider`. */
export interface LoadingProviderSnapshot {
  stackSmid: string;
  /** `provider.msSinceLastFrameDelivered()` — null if this stack has never delivered a frame. */
  msSinceLastFrameDelivered: number | null;
}

/** Warm-path gate: mirrors `PixelWorkerConnection.DELIVERY_STALL_MS` (the worker-side twin). */
export const IDLE_DELIVERY_STALL_MS = 120_000;

/**
 * Cold-start gate for a stack that has never delivered a single frame: mirrors
 * `PixelWorkerConnection.COLD_START_STALL_MS`.
 */
export const IDLE_COLD_START_STALL_MS = 180_000;

/**
 * Drops bookkeeping for stacks no longer `loading` and stamps a first-seen time for any newly
 * loading stack, so the cold-start gate below has a clock to measure from — providers expose no
 * "load started at" timestamp of their own. Mutates `firstSeenLoadingAt` in place.
 */
export function updateFirstSeenLoadingAt(
  loadingStackSmids: ReadonlySet<string>,
  firstSeenLoadingAt: Map<string, number>,
  now: number
): void {
  for (const stackSmid of firstSeenLoadingAt.keys()) {
    if (!loadingStackSmids.has(stackSmid)) firstSeenLoadingAt.delete(stackSmid);
  }
  for (const stackSmid of loadingStackSmids) {
    if (!firstSeenLoadingAt.has(stackSmid)) firstSeenLoadingAt.set(stackSmid, now);
  }
}

/**
 * Returns the stackSmid of the first `loading` provider whose delivery has stalled past the
 * appropriate gate, or null if every loading provider is either delivering normally or too
 * recently started to judge. `firstSeenLoadingAt` must already reflect `now` via a prior call to
 * {@link updateFirstSeenLoadingAt}.
 */
export function findStalledLoadingStack(
  providers: readonly LoadingProviderSnapshot[],
  firstSeenLoadingAt: ReadonlyMap<string, number>,
  now: number
): string | null {
  for (const { stackSmid, msSinceLastFrameDelivered } of providers) {
    if (msSinceLastFrameDelivered != null) {
      if (msSinceLastFrameDelivered > IDLE_DELIVERY_STALL_MS) return stackSmid;
      continue;
    }
    const firstSeen = firstSeenLoadingAt.get(stackSmid);
    if (firstSeen != null && now - firstSeen > IDLE_COLD_START_STALL_MS) return stackSmid;
  }
  return null;
}
